import type { SensorStatus } from "@sitescene/types";

import { AlertTriangle, MarkerPin01, WifiOff } from "@untitledui/icons";
import mapboxgl from "mapbox-gl";
import {
	type CSSProperties,
	type MouseEvent,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";

import { cx } from "@/utils/cx";

import { SENSOR_TYPE_ICON_MAP } from "../../utils/sensor/sensor-type";
import { isValidCoordinatePair } from "../../utils/shared/coordinates";
import { ClusterPillMarker } from "./cluster-pill-marker";
import { isClusterMarkerFeature } from "./create-marker-feature";

// Keyed by SensorStatus enum values so a future rename of the enum
// shifts these keys in lockstep instead of silently falling through
// to DEFAULT_STYLE for every marker.
const STATUS_STYLES: Record<
	SensorStatus,
	{
		activeColor: string;
		defaultColor: string;
		icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
	}
> = {
	["active"]: {
		activeColor: "fill-brand-700 stroke-brand-600",
		defaultColor: "fill-brand-500 stroke-brand-400",
	},
	["offline"]: {
		activeColor: "fill-utility-red-700 stroke-utility-red-600",
		defaultColor: "fill-utility-red-500 stroke-utility-red-400",
		icon: WifiOff,
	},
	["warning"]: {
		activeColor: "fill-utility-yellow-600 stroke-utility-yellow-500",
		defaultColor: "fill-utility-yellow-500 stroke-utility-yellow-400",
		icon: AlertTriangle,
	},
};

const SENSOR_STATUS_VALUES: readonly string[] = [
	"active",
	"offline",
	"warning",
];

const isSensorStatus = (value: unknown): value is SensorStatus =>
	typeof value === "string" && SENSOR_STATUS_VALUES.includes(value);

const numericFeatureProp = (value: unknown): number =>
	typeof value === "number" && Number.isFinite(value) ? value : 0;

const DEFAULT_STYLE = {
	activeColor: "fill-utility-brand-700 stroke-utility-brand-700",
	defaultColor: "fill-utility-brand-500 stroke-utility-brand-500",
};

const DEFAULT_CENTER: [number, number] = [-104.9903, 39.7392];

/** Delay single-click when dblclick-to-edit is enabled (avoids mid-gesture map fit). */
const DOUBLE_CLICK_GUARD_MS = 150;

/**
 * One map pin or location/zone card.
 *
 * Live alerts tint the pin (or the cluster card) with that alert's priority
 * color. After the pulse, the pin stays that color for the rest of the session.
 */
interface MapMarkerProps {
	draggable?: boolean;
	feature: GeoJSON.Feature;
	/** How many live alerts belong to this cluster (ignored for sensor pins). */
	flashAlertCount?: number;
	/** True while a newly arrived alert is pulsing this marker. */
	flashIsFlashing?: boolean;
	/** Priority color: latest live alert on pins, highest on clusters. */
	flashSeverity?: number;
	interactive?: boolean;
	isActive: boolean;
	/**
	 * Alert-list hover: grow this pin so the operator can see which sensors
	 * belong to the hovered event. Cluster cards ignore this.
	 */
	isEmphasized?: boolean;
	map: mapboxgl.Map;
	onClick: (feature: GeoJSON.Feature) => void;
	onContextMenu?: (
		feature: GeoJSON.Feature,
		point: { x: number; y: number },
	) => void;
	onDoubleClick?: (feature: GeoJSON.Feature) => void;
	onDragEnd?: (coordinates: [number, number]) => void;
	onHoverEnd?: () => void;
	onHoverStart?: () => void;
}

export const MapMarker = ({
	draggable = false,
	feature,
	flashAlertCount,
	flashIsFlashing,
	flashSeverity,
	interactive = true,
	isActive,
	isEmphasized = false,
	map,
	onClick,
	onContextMenu,
	onDoubleClick,
	onDragEnd,
	onHoverEnd,
	onHoverStart,
}: MapMarkerProps) => {
	const {
		geometry = { coordinates: [DEFAULT_CENTER], type: "Point" },
		properties,
	} = feature;

	const markerRef = useRef<mapboxgl.Marker | null>(null);
	const focusableRef = useRef<HTMLDivElement | null>(null);
	const wasActiveRef = useRef(isActive);
	const pendingClickTimeoutRef = useRef<null | number>(null);
	const [containerElement] = useState(() => document.createElement("div"));
	const [isHovered, setIsHovered] = useState(false);

	const isCluster = isClusterMarkerFeature(feature);
	const isDimmed = properties?.dimmed === true;
	const isFlashing = Boolean(flashIsFlashing);
	const showAsDimmed = isDimmed && !isHovered && !isEmphasized;
	// Keep the priority color after the pulse if this session still has live alerts here.
	const hasAlertColor =
		flashSeverity !== undefined && (isFlashing || (flashAlertCount ?? 0) > 0);
	const flashStyle = hasAlertColor ? ({} as CSSProperties) : undefined;

	// `properties.status` comes from a GeoJSON feature so it is typed as
	// unknown here; normalise to lower-case and narrow to SensorStatus before
	// indexing STATUS_STYLES so the coupling between marker colors and the
	// SensorStatus enum is enforced at the type level.
	const rawStatus =
		typeof properties?.status === "string"
			? properties.status.toLowerCase()
			: undefined;
	const statusStyle = isSensorStatus(rawStatus)
		? STATUS_STYLES[rawStatus]
		: undefined;

	const SensorIcon = useMemo(() => {
		if (statusStyle?.icon) return statusStyle.icon;
		const type = properties?.type as string | undefined;
		if (!type) return MarkerPin01;
		return SENSOR_TYPE_ICON_MAP[type.toLowerCase()] ?? MarkerPin01;
	}, [statusStyle?.icon, properties?.type]);

	const [lng, lat] =
		geometry.type === "Point"
			? (geometry.coordinates as [number, number])
			: DEFAULT_CENTER;

	useEffect(() => {
		if (geometry.type !== "Point") {
			return;
		}

		const hasValidCoords = isValidCoordinatePair([lng, lat]);

		if (!hasValidCoords) {
			console.warn(
				`Invalid coordinates for marker: [${lng}, ${lat}]. Using default center.`,
			);
		}

		if (isCluster) {
			containerElement.classList.add("map-cluster-marker");
		}

		markerRef.current = new mapboxgl.Marker({
			anchor: "bottom",
			draggable: draggable && !isCluster,
			element: containerElement,
		})
			.setLngLat(hasValidCoords ? [lng, lat] : DEFAULT_CENTER)
			.addTo(map);

		const marker = markerRef.current;
		const handleDragEnd = () => {
			const { lat: nextLat, lng: nextLng } = marker.getLngLat();
			onDragEnd?.([nextLng, nextLat]);
		};

		if (draggable && !isCluster) {
			marker.on("dragend", handleDragEnd);
		}

		return () => {
			if (draggable && !isCluster) {
				marker.off("dragend", handleDragEnd);
			}
			marker.remove();
		};
	}, [
		containerElement,
		draggable,
		geometry.type,
		isCluster,
		// Recreate the pin only when the coordinates change, not when the
		// parent rebuilds the same feature object (Locate would otherwise jitter).
		lat,
		lng,
		map,
		onDragEnd,
	]);

	// Elevate the active marker above sibling markers and nearby
	// map overlays. Mapbox marker wrappers commonly use z-index values
	// in the 0–2 range, so bump to 10 to guarantee the active marker
	// stays on top without clashing with app-level modal/tooltip layers.
	useEffect(() => {
		// Access the element via markerRef to keep mutation off the useState value
		const el = markerRef.current?.getElement();
		if (el) {
			el.style.zIndex = isHovered
				? "15"
				: isEmphasized
					? "12"
					: isFlashing
						? "11"
						: isActive
							? "10"
							: "";
			el.style.cursor =
				draggable && !isCluster && !isDimmed
					? "grab"
					: interactive
						? "pointer"
						: "";
			// Opacity is handled on the pin content so hover can ease it back up
			// without stacking a second fade on the Mapbox wrapper.
			el.style.opacity = "";
		}
	}, [
		draggable,
		interactive,
		isActive,
		isCluster,
		isDimmed,
		isEmphasized,
		isFlashing,
		isHovered,
	]);

	useEffect(() => {
		if (!isCluster || !interactive) return;

		const el = markerRef.current?.getElement();
		if (!el) return;

		const handleMouseEnter = () => setIsHovered(true);
		const handleMouseLeave = () => setIsHovered(false);

		el.addEventListener("mouseenter", handleMouseEnter);
		el.addEventListener("mouseleave", handleMouseLeave);

		return () => {
			el.removeEventListener("mouseenter", handleMouseEnter);
			el.removeEventListener("mouseleave", handleMouseLeave);
		};
	}, [interactive, isCluster]);

	// When the popup closes (marker goes inactive), Mapbox removes its close
	// button and focus falls back to <body>, so the next Tab restarts from the
	// top of the page. Return focus to the marker so tab order resumes here.
	// Guarded on focus actually being lost so clicking another marker (which
	// focuses that marker) doesn't yank focus back.
	useEffect(() => {
		const wasActive = wasActiveRef.current;
		wasActiveRef.current = isActive;

		if (!interactive || !wasActive || isActive) return;

		const focusLost =
			!document.activeElement || document.activeElement === document.body;
		if (focusLost) {
			focusableRef.current?.focus();
		}
	}, [interactive, isActive]);

	useEffect(() => {
		return () => {
			if (pendingClickTimeoutRef.current !== null) {
				window.clearTimeout(pendingClickTimeoutRef.current);
			}
		};
	}, []);

	const clearPendingClick = () => {
		if (pendingClickTimeoutRef.current === null) return;
		window.clearTimeout(pendingClickTimeoutRef.current);
		pendingClickTimeoutRef.current = null;
	};

	/**
	 * When double-click is used for edit, delay the single-click handler so the
	 * first click of a double-click does not refit/zoom the map under the cursor.
	 */
	const handleMarkerClick = (event: MouseEvent<HTMLDivElement>) => {
		event.stopPropagation();
		if (event.detail > 1) return;

		if (!onDoubleClick) {
			onClick(feature);
			return;
		}

		clearPendingClick();
		pendingClickTimeoutRef.current = window.setTimeout(() => {
			pendingClickTimeoutRef.current = null;
			onClick(feature);
		}, DOUBLE_CLICK_GUARD_MS);
	};

	const handleMarkerDoubleClick = (event: MouseEvent<HTMLDivElement>) => {
		event.stopPropagation();
		event.preventDefault();
		clearPendingClick();
		onDoubleClick?.(feature);
	};

	const { activeColor, defaultColor } = statusStyle ?? DEFAULT_STYLE;
	const iconClassName = isActive ? activeColor : defaultColor;

	if (isCluster) {
		const count = typeof properties?.count === "number" ? properties.count : 0;
		const name =
			typeof properties?.name === "string" ? properties.name : "Location";
		const missingSetup = Array.isArray(properties?.missingSetup)
			? (properties.missingSetup as string[]).filter(
					(gap): gap is "installation" | "sensors" | "zone" =>
						gap === "installation" || gap === "sensors" || gap === "zone",
				)
			: undefined;

		return (
			<>
				{createPortal(
					<ClusterPillMarker
						count={count}
						dimmed={showAsDimmed}
						interactive={interactive}
						isHovered={isHovered || isEmphasized}
						missingSetup={missingSetup}
						name={name}
						offline={numericFeatureProp(properties?.sensorOffline)}
						onClick={() => onClick(feature)}
						online={numericFeatureProp(properties?.sensorOnline)}
						warning={numericFeatureProp(properties?.sensorWarning)}
					/>,
					containerElement,
				)}
			</>
		);
	}

	return (
		<>
			{createPortal(
				<div
					className={cx(
						"relative inline-flex h-10 w-9 origin-bottom transition-[opacity,transform,scale,filter] duration-200 ease-out",
						isEmphasized && "scale-120",
						showAsDimmed ? "opacity-60 saturate-50" : "opacity-100",
						draggable && !isCluster && !isDimmed
							? "cursor-grab active:cursor-grabbing"
							: interactive
								? "cursor-pointer"
								: "pointer-events-none",
					)}
					data-emphasized={isEmphasized ? "true" : undefined}
					data-flashing={isFlashing ? "true" : undefined}
					onClick={interactive ? handleMarkerClick : undefined}
					onContextMenu={
						interactive && onContextMenu
							? (event) => {
									event.preventDefault();
									event.stopPropagation();
									onContextMenu(feature, {
										x: event.clientX,
										y: event.clientY,
									});
								}
							: undefined
					}
					onDoubleClick={
						interactive && onDoubleClick ? handleMarkerDoubleClick : undefined
					}
					onKeyDown={
						interactive
							? (e) => {
									if (e.key === "Enter" || e.key === " ") {
										onClick(feature);
									}
								}
							: undefined
					}
					onMouseEnter={
						interactive
							? () => {
									setIsHovered(true);
									onHoverStart?.();
								}
							: undefined
					}
					onMouseLeave={
						interactive
							? () => {
									setIsHovered(false);
									onHoverEnd?.();
								}
							: undefined
					}
					ref={focusableRef}
					role={interactive ? "button" : undefined}
					style={flashStyle}
					tabIndex={interactive ? 0 : undefined}
				>
					{/* Pulse while the alert is new; keep the priority fill after the pulse. */}
					<svg
						className={cx(
							isFlashing
								? "map-sensor-pulse-pin"
								: hasAlertColor
									? "map-sensor-alert-pin"
									: iconClassName,
							"h-10 w-9 drop-shadow-md",
						)}
						viewBox="0 0 36 41"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path d="M18 0.5C27.665 0.5 35.5 8.33502 35.5 18C35.5 25.6438 30.5992 32.1447 23.7666 34.5283C23.1569 34.741 22.607 35.3011 22.125 35.8955C21.6294 36.5067 21.1248 37.2609 20.6387 37.957C20.142 38.6684 19.6616 39.3244 19.1875 39.8018C18.699 40.2936 18.3089 40.5 18 40.5C17.6911 40.5 17.301 40.2936 16.8125 39.8018C16.3384 39.3244 15.858 38.6684 15.3613 37.957C14.8752 37.2609 14.3706 36.5067 13.875 35.8955C13.393 35.3011 12.8431 34.741 12.2334 34.5283C5.40084 32.1447 0.5 25.6438 0.5 18C0.5 8.33502 8.33502 0.5 18 0.5Z" />
					</svg>
					<SensorIcon className="absolute top-[18px] left-1/2 h-[16px] w-[16px] -translate-x-1/2 -translate-y-1/2 text-white" />
				</div>,
				containerElement,
			)}
		</>
	);
};
